import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle, Heart } from "lucide-react";
import { Reveal } from "./Motion";
import { useLanguage } from "../i18n/LanguageContext";
import { waLink, pad } from "../lib/site";

export const PageHeader = ({ overline, title, sub, badge, overlineTestId }) => (
  <section className="border-b border-warmline bg-white pt-28 pb-12 sm:pt-32 sm:pb-14" data-testid="detail-hero">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <p
        className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.25em] text-teal-dark"
        data-testid={overlineTestId || "detail-overline"}
      >
        <span className="h-px w-8 bg-gold" aria-hidden="true" />
        {overline}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="max-w-3xl font-display text-4xl font-semibold leading-tight tracking-tight text-petrol sm:text-5xl" data-testid="detail-title">
          {title}
        </h1>
        {badge && (
          <span className="rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-petrol" data-testid="detail-badge">
            {badge}
          </span>
        )}
      </div>
      {sub && (
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate2 sm:text-lg" data-testid="detail-teaser">
          {sub}
        </p>
      )}
    </div>
  </section>
);

export const CtaBand = ({ testId = "detail-cta-band", ctaTestId = "detail-whatsapp-cta" }) => {
  const { t } = useLanguage();
  return (
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl bg-teal p-8 sm:p-12" data-testid={testId}>
        <Heart className="pointer-events-none absolute -right-6 -top-8 h-44 w-44 text-white/10" aria-hidden="true" />
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">{t.home.ctaBand.title}</h2>
            <p className="mt-3 text-white/85">{t.home.ctaBand.text}</p>
          </div>
          <a
            href={waLink(t.wa.message)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.common.whatsappAria}
            data-testid={ctaTestId}
            className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-petrol transition-colors hover:bg-white"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            {t.home.ctaBand.button}
          </a>
        </div>
      </div>
    </Reveal>
  );
};

const DetailPage = ({ data, basePath, relatedItems, relatedBasePath }) => {
  const { t } = useLanguage();
  return (
    <main id="main-content">
      <PageHeader overline={data.overline} badge={data.badge} title={data.title} sub={data.teaser} />

      <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8 sm:py-16">
        <Link
          to={basePath}
          data-testid="detail-back-link"
          className="inline-flex items-center gap-2 text-sm font-bold text-teal-dark transition-colors hover:text-petrol"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {basePath === "/themen" ? t.common.backToTopics : t.common.backToMethods}
        </Link>

        <div className="mt-10 divide-y divide-warmline border-y border-warmline">
          {data.sections.map((section, i) => (
            <Reveal key={i} delay={0.04}>
              <article className="grid gap-4 py-10 sm:grid-cols-[5rem_1fr] sm:gap-8" data-testid={`detail-section-${i}`}>
                <span className="font-mono text-sm font-semibold text-gold-dark" aria-hidden="true">
                  ({pad(i + 1)})
                </span>
                <div>
                  <h2 className="font-display text-2xl font-semibold tracking-tight text-petrol sm:text-3xl">{section.h}</h2>
                  <div className="mt-4 space-y-4 text-base leading-relaxed text-slate2 sm:text-lg">
                    {section.paras.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                  {section.list && (
                    <ul className="mt-6 flex flex-wrap gap-2" data-testid={`detail-list-${i}`}>
                      {section.list.map((item, k) => (
                        <li key={k} className="rounded-full border border-teal/30 bg-teal-light px-3.5 py-1.5 text-sm font-medium text-petrol">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {relatedItems && relatedItems.length > 0 && (
          <Reveal className="mt-12" delay={0.04}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold-dark" data-testid="related-label">
              {t.common.related}
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {relatedItems.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`${relatedBasePath}/${rel.slug}`}
                  data-testid={`related-link-${rel.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-warmline bg-white px-4 py-2 text-sm font-medium text-petrol transition-colors hover:border-teal hover:text-teal-dark"
                >
                  {rel.title}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Reveal>
        )}

        <div className="mt-14">
          <CtaBand />
        </div>
      </div>
    </main>
  );
};

export default DetailPage;
